import type {
  IntegrationAccountGroup,
  IntegrationIdentity,
  IntegrationProperties,
} from "@/shared/integrations/integration.types";
import {
  canCaptureAnalytics,
  captureAnalytics,
  groupIntegrationAccount,
  identifyIntegrations,
  isAnalyticsConfigured,
  resetIntegrations,
} from "@/shared/integrations/integration-runtime";
import { shouldResetBeforeIdentify } from "@/shared/integrations/posthog.integration";

export { shouldResetBeforeIdentify };
export const isAnalyticsEnabled = isAnalyticsConfigured;

export type RegistrationMethod = "email" | "magic_link" | "google" | "invitation";

export interface AnalyticsEventMap {
  signup_started: { method: RegistrationMethod };
  signup_verification_requested: { method: "email" };
  signup_verified: { method: RegistrationMethod };
  registration_completed: { method: RegistrationMethod };
  activation_team_added: { method: "import" | "manual" | "invitation" };
  activation_kudos_created: undefined;
  activation_check_in_completed: undefined;
  activation_completed: undefined;
  vppc_review_rating_selected: undefined;
  vppc_generate_cycle_picked: { picked: boolean };
  vppc_plan_activated: undefined;
  vppc_clawback_toggled: { on: boolean };
  vppc_plan_created: { scenario: string };
  vppc_scenario_selected: { scenario: string };
  vppc_nudge_shown: { nudge: string };
  vppc_nudge_dismissed: { nudge: string };
  vppc_rating_percent_changed: { rating: number; percent: number };
}

type EventArguments<Name extends keyof AnalyticsEventMap> =
  AnalyticsEventMap[Name] extends undefined ? [] : [properties: AnalyticsEventMap[Name]];

/** Typed, vendor-neutral product-event boundary. */
export function captureEvent<Name extends keyof AnalyticsEventMap>(
  name: Name,
  ...args: EventArguments<Name>
): void {
  captureAnalytics(name, args[0] as IntegrationProperties | undefined);
}

export interface PageViewInput {
  path: string;
  routeId: string;
  workspaceId: string | null;
}

export function capturePageView({ path, routeId, workspaceId }: PageViewInput): void {
  captureAnalytics("$pageview", {
    path,
    route_id: routeId,
    workspace_id: workspaceId,
  });
}

export type IdentityInput = IntegrationIdentity;

export function identifyUser(identity: IdentityInput): void {
  identifyIntegrations(identity);
}

export type AccountGroupInput = IntegrationAccountGroup;

export function setAccountGroup(group: AccountGroupInput): void {
  groupIntegrationAccount(group);
}

export function canSendAnalytics(): boolean {
  return canCaptureAnalytics();
}

export async function resetAnalytics(): Promise<void> {
  await resetIntegrations();
}
